/**
 * Chat message area components: suggestions display and message list content.
 */

import type { UIMessage } from "@convex-dev/agent/react";
import { cn } from "@plasma/utils/cn";
import { Lightbulb, Sparkles, Zap } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Spinner } from "~/components/ui/spinner";

import { MessageBubble } from "./message-bubble";

/** Renders suggestions based on loading/data state */
export function SuggestionsContent({
  isLoading,
  suggestions,
  onSuggestionClick,
}: {
  isLoading: boolean;
  suggestions: Array<{ category: string; question: string }>;
  onSuggestionClick: (question: string) => void;
}) {
  if (isLoading) {
    return (
      <div className="text-muted-foreground flex items-center justify-center gap-2 py-4 text-xs">
        <Spinner className="h-3 w-3" />
        <span>Thinking of suggestions...</span>
      </div>
    );
  }

  if (suggestions.length > 0) {
    return (
      <div className="flex flex-col gap-2">
        {suggestions.map((suggestion) => (
          <button
            className={cn(
              "flex items-center gap-2 rounded-lg px-3 py-2 text-left text-sm",
              "border-border/50 bg-background/50 border",
              "hover:border-accent-foreground/20 hover:bg-accent",
              "transition-colors duration-150"
            )}
            key={`${suggestion.category}-${suggestion.question}`}
            onClick={() => onSuggestionClick(suggestion.question)}
            type="button"
          >
            {suggestion.category === "quick" && (
              <Zap className="text-primary h-3.5 w-3.5 flex-shrink-0" />
            )}
            {suggestion.category === "insight" && (
              <Lightbulb className="text-warning h-3.5 w-3.5 flex-shrink-0" />
            )}
            {suggestion.category === "action" && (
              <Sparkles className="text-info h-3.5 w-3.5 flex-shrink-0" />
            )}
            <span className="text-foreground/80">{suggestion.question}</span>
          </button>
        ))}
      </div>
    );
  }

  return (
    <p className="text-muted-foreground/50 py-2 text-center text-xs">
      Try asking about your sales, menu, or employees
    </p>
  );
}

/** Renders the message area content based on ready/messages state */
export function MessageAreaContent({
  isReady,
  messages,
  status,
  loadMore,
  messagesEndRef,
  isSuggestionsLoading,
  suggestions,
  handleSuggestionClick,
}: {
  isReady: boolean;
  messages: UIMessage[] | undefined;
  status: string;
  loadMore: (count: number) => void;
  messagesEndRef: React.RefObject<HTMLDivElement | null>;
  isSuggestionsLoading: boolean;
  suggestions: Array<{ category: string; question: string }>;
  handleSuggestionClick: (question: string) => void;
}) {
  // Not ready - show spinner
  if (!isReady) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }

  // Has messages - show conversation
  if (messages && messages.length > 0) {
    return (
      <div className="flex flex-col gap-4">
        {status === "CanLoadMore" && (
          <div className="flex justify-center">
            <Button
              onClick={() => loadMore(20)}
              size="sm"
              type="button"
              variant="ghost"
            >
              Load earlier messages
            </Button>
          </div>
        )}
        {messages.map((message) => (
          <MessageBubble key={message.key} message={message} />
        ))}
        <div ref={messagesEndRef} />
      </div>
    );
  }

  // Empty state - show welcome and suggestions
  return (
    <div className="text-muted-foreground flex h-full flex-col items-center justify-center text-center">
      <p className="mb-2 text-lg font-medium">Hi! I'm Plasma AI</p>
      <p className="mb-6 max-w-xs text-sm">
        Ask me about your sales, menu items, employees, or anything else about
        your business.
      </p>

      {/* V4.1: LLM-generated dynamic suggestions */}
      <div className="w-full max-w-sm space-y-2">
        <p className="text-muted-foreground/70 text-xs font-medium tracking-wide uppercase">
          Suggested questions
        </p>
        <SuggestionsContent
          isLoading={isSuggestionsLoading}
          onSuggestionClick={handleSuggestionClick}
          suggestions={suggestions}
        />
      </div>
    </div>
  );
}
